if (!customElements.get("m-newsletter-popup")) {
  customElements.define(
    "m-newsletter-popup",
    class NewsletterPopup extends HTMLElement {
      constructor() {
        super();

        this.storageKey = `newsletter-popup-${this.dataset.sectionId}`;
        this.closeButtons = this.querySelectorAll("[data-newsletter-popup-close]");
        this.dialog = this.querySelector(".m-newsletter-popup__dialog");
        this.form = this.querySelector("form");
        this.resultNotification = this.querySelector(".notification.show");
        this.successMessage = this.dataset.successMessage || "Thank you for subscribing. The discount code has been sent to your email.";
        this.delay = Number(this.dataset.delay || 0);
        this.frequency = this.dataset.frequency || "days";
        this.days = Number(this.dataset.days || 14);
        this.isDesignMode = this.dataset.designMode === "true" && window.Shopify && window.Shopify.designMode;
        this.isEnabled = this.dataset.enable === "true";
        this.openTimeout = null;
        this.previouslyFocused = null;
      }

      connectedCallback() {
        this.closeButtons.forEach((button) => {
          button.addEventListener("click", this.close.bind(this));
        });

        this.addEventListener("keydown", this.handleKeydown.bind(this));

        if (this.form) {
          this.form.addEventListener("submit", this.handleSubmit.bind(this));
        }

        if (!this.isEnabled && !this.isDesignMode) return;

        if (this.resultNotification) {
          this.resultNotification.remove();
          this.resultNotification = null;
        }

        if (!this.isDesignMode && this.hasBeenDismissed()) return;

        this.openTimeout = window.setTimeout(() => this.open(), this.isDesignMode ? 200 : this.delay);
      }

      disconnectedCallback() {
        window.clearTimeout(this.openTimeout);
        document.body.classList.remove("m-newsletter-popup-open");
      }

      open() {
        this.previouslyFocused = document.activeElement;
        this.classList.add("is-open");
        document.body.classList.add("m-newsletter-popup-open");
        this.dialog && this.dialog.focus();
      }

      close() {
        this.classList.remove("is-open");
        document.body.classList.remove("m-newsletter-popup-open");

        if (!this.isDesignMode) {
          this.saveDismissedState();
        }

        if (this.previouslyFocused && typeof this.previouslyFocused.focus === "function") {
          this.previouslyFocused.focus();
        }
      }

      handleKeydown(event) {
        if (event.key === "Escape" && this.classList.contains("is-open")) {
          this.close();
        }
      }

      handleSubmit(event) {
        event.preventDefault();

        if (!this.form || this.form.classList.contains("is-submitting")) return;

        const submitButton = this.form.querySelector('[type="submit"]');
        const formData = new FormData(this.form);
        this.form.classList.add("is-submitting");
        submitButton && submitButton.classList.add("m-spinner-loading");

        fetch(this.form.action, {
          method: this.form.method || "POST",
          body: formData,
          credentials: "same-origin",
          headers: {
            Accept: "text/html",
          },
        })
          .then((response) => response.text().then((html) => ({ html, responseUrl: response.url })))
          .then(({ html, responseUrl }) => {
            const doc = new DOMParser().parseFromString(html, "text/html");
            const returnedForm = doc.getElementById(this.form.id);
            const returnedNotification = returnedForm && returnedForm.querySelector(".notification.show");
            const isSuccess =
              Boolean(returnedNotification && returnedNotification.classList.contains("success")) ||
              html.includes("posted_successfully") ||
              html.includes("customer_posted=true") ||
              responseUrl.includes("customer_posted=true");

            if (isSuccess) {
              this.renderResult("success", this.successMessage);
              return;
            }

            const errorMessage =
              returnedNotification && returnedNotification.textContent.trim()
                ? returnedNotification.textContent.trim()
                : "Please check your email address and try again.";
            this.renderResult("warning", errorMessage);
          })
          .catch(() => {
            this.renderResult("warning", "Please check your email address and try again.");
          })
          .finally(() => {
            this.form.classList.remove("is-submitting");
            submitButton && submitButton.classList.remove("m-spinner-loading");
          });
      }

      renderResult(type, message) {
        const existingNotification = this.form.querySelector(".notification.show");
        const wrapper = this.form.querySelector(".m-newsletter-form__wrapper");
        const agreement = this.form.querySelector(".m-newsletter-form__agreement-text");

        if (existingNotification) existingNotification.remove();

        const notification = document.createElement("div");
        notification.className = `notification show ${type}`;
        notification.innerHTML = `<div class="notification--error-message">${this.escapeHTML(message)}</div>`;
        this.form.prepend(notification);

        if (type === "success") {
          wrapper && wrapper.setAttribute("hidden", "");
          agreement && agreement.setAttribute("hidden", "");
        } else {
          wrapper && wrapper.removeAttribute("hidden");
          agreement && agreement.removeAttribute("hidden");
        }

        this.open();
      }

      escapeHTML(value) {
        const div = document.createElement("div");
        div.textContent = value;
        return div.innerHTML;
      }

      hasBeenDismissed() {
        if (this.frequency === "always") return false;

        try {
          if (this.frequency === "session") {
            return sessionStorage.getItem(this.storageKey) === "dismissed";
          }

          const storedValue = localStorage.getItem(this.storageKey);
          if (!storedValue) return false;

          if (this.frequency === "once") return storedValue === "dismissed";

          const expiry = Number(storedValue);
          return expiry && Date.now() < expiry;
        } catch (error) {
          return false;
        }
      }

      saveDismissedState() {
        if (this.frequency === "always") return;

        try {
          if (this.frequency === "session") {
            sessionStorage.setItem(this.storageKey, "dismissed");
            return;
          }

          if (this.frequency === "once") {
            localStorage.setItem(this.storageKey, "dismissed");
            return;
          }

          const expiry = Date.now() + this.days * 24 * 60 * 60 * 1000;
          localStorage.setItem(this.storageKey, String(expiry));
        } catch (error) {
          return;
        }
      }
    }
  );
}
